import { useEffect, useLayoutEffect, useRef, useState } from "react";

const SPATIAL_INTELLIGENCE_STAGES = [
  { percent: 0, label: "AI Causal Blindness" },
  { percent: 25, label: "Spatiotemporal Dynamics Recognized" },
  { percent: 50, label: "Causal & Object Relationships Formed" },
  { percent: 75, label: "Patterned World Model AI" },
  { percent: 100, label: "Full Spatial Intelligence" },
];

export function SceneBlurSlider({
  value = 0,
  onChange,
  onExpandedChange,
  isCollapsed: externallyCollapsed = false,
}) {
  const normalizedValue = Math.max(0, Math.min(100, Number(value) || 0));
  const spatialStage = [...SPATIAL_INTELLIGENCE_STAGES]
    .reverse()
    .find((stage) => normalizedValue >= stage.percent);
  const spatialStageWords = spatialStage.label.split(" ");
  const [isManuallyCollapsed, setIsManuallyCollapsed] = useState(true);
  const openToggleRef = useRef(null);
  const hasShownMovementHint = useRef(false);
  const movementHintFrame = useRef(null);
  const isCollapsed = externallyCollapsed || isManuallyCollapsed;

  useLayoutEffect(() => {
    if (typeof document === "undefined") return undefined;

    const syncExpandAnchor = () => {
      const toggle = openToggleRef.current;
      if (!toggle) return;

      const rect = toggle.getBoundingClientRect();
      if (!rect.width) return;

      document.documentElement.style.setProperty(
        "--scene-blur-expand-x",
        `${rect.left + rect.width / 2}px`,
      );
    };

    syncExpandAnchor();
    return undefined;
  }, [isCollapsed]);

  useEffect(() => {
    onExpandedChange?.(!isCollapsed);
  }, [isCollapsed, onExpandedChange]);

  useEffect(() => {
    if (isCollapsed) {
      if (movementHintFrame.current !== null) {
        window.cancelAnimationFrame(movementHintFrame.current);
        movementHintFrame.current = null;
      }
      return undefined;
    }
    if (hasShownMovementHint.current) return undefined;

    hasShownMovementHint.current = true;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      onChange?.(80);
      return undefined;
    }

    const duration = 2400;
    const restingValue = 80;
    const movementRange = 15;
    let startedAt = null;

    const animateHint = (timestamp) => {
      if (startedAt === null) startedAt = timestamp;
      const progress = Math.min(1, (timestamp - startedAt) / duration);
      const nextValue = restingValue + Math.sin(progress * Math.PI * 4) * movementRange;
      onChange?.(Math.round(nextValue));

      if (progress < 1) {
        movementHintFrame.current = window.requestAnimationFrame(animateHint);
        return;
      }

      movementHintFrame.current = null;
      onChange?.(restingValue);
    };

    movementHintFrame.current = window.requestAnimationFrame(animateHint);
    return () => {
      if (movementHintFrame.current !== null) {
        window.cancelAnimationFrame(movementHintFrame.current);
        movementHintFrame.current = null;
      }
    };
  }, [isCollapsed, onChange]);

  const stopMovementHint = () => {
    if (movementHintFrame.current !== null) {
      window.cancelAnimationFrame(movementHintFrame.current);
      movementHintFrame.current = null;
    }
  };

  const updateFromPointer = (event) => {
    stopMovementHint();
    const track = event.currentTarget.parentElement;
    const rect = track.getBoundingClientRect();
    const position = Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height));
    onChange?.(Math.round((1 - position) * 100));
  };

  const handleMetricPointerDown = (event) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    updateFromPointer(event);
  };

  const handleMetricPointerMove = (event) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      updateFromPointer(event);
    }
  };

  const handleMetricPointerUp = (event) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleMetricKeyDown = (event) => {
    const step = event.shiftKey ? 10 : 1;
    let nextValue = normalizedValue;

    if (event.key === "ArrowUp" || event.key === "PageUp") {
      nextValue = Math.min(100, normalizedValue + (event.key === "PageUp" ? 10 : step));
    } else if (event.key === "ArrowDown" || event.key === "PageDown") {
      nextValue = Math.max(0, normalizedValue - (event.key === "PageDown" ? 10 : step));
    } else if (event.key === "Home") {
      nextValue = 0;
    } else if (event.key === "End") {
      nextValue = 100;
    } else {
      return;
    }

    event.preventDefault();
    stopMovementHint();
    onChange?.(nextValue);
  };

  return (
    <div
      className={`scene-blur-control${isCollapsed ? " is-collapsed" : ""}`}
      aria-label="Scene focus control"
      onPointerDown={(event) => event.stopPropagation()}
      onPointerMove={(event) => event.stopPropagation()}
      onTouchStart={(event) => event.stopPropagation()}
      onTouchMove={(event) => event.stopPropagation()}
    >
      <span className="scene-blur-control__label scene-blur-control__label--clear">
        How
        <br />
        Humans Sense
        <br />
        the World
      </span>
      <div
        className="scene-blur-control__track"
        style={{ "--scene-blur-thumb-position": `${100 - normalizedValue}%` }}
      >
        <span className="scene-blur-control__endpoint scene-blur-control__endpoint--top" aria-hidden="true" />
        <span className="scene-blur-control__endpoint scene-blur-control__endpoint--bottom" aria-hidden="true" />
        <input
          className="scene-blur-control__range"
          type="range"
          min="0"
          max="100"
          step="1"
          value={normalizedValue}
          aria-label="Scene blur amount"
          aria-valuetext={`${normalizedValue}% blur`}
          onChange={(event) => {
            stopMovementHint();
            onChange?.(Number(event.target.value));
          }}
          style={{ "--scene-blur-progress": `${normalizedValue}%` }}
        />
        <output
          className="scene-blur-control__value"
          tabIndex={0}
          style={{
            "--scene-blur-thumb-position": `${100 - normalizedValue}%`,
          }}
          onPointerDown={(event) => {
            handleMetricPointerDown(event);
          }}
          onPointerMove={handleMetricPointerMove}
          onPointerUp={(event) => {
            event.currentTarget.releasePointerCapture(event.pointerId);
          }}
          onPointerCancel={(event) => {
            event.currentTarget.releasePointerCapture(event.pointerId);
          }}
        >
          <strong>{normalizedValue}%</strong>
          <span>
            Spatial
            <br />
            Intelligence
          </span>
        </output>
        <span
          className="scene-blur-control__state-group"
          role="slider"
          tabIndex={0}
          aria-label="Scene blur amount"
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow={normalizedValue}
          aria-valuetext={`${normalizedValue}% blur`}
          onPointerDown={handleMetricPointerDown}
          onPointerMove={handleMetricPointerMove}
          onPointerUp={handleMetricPointerUp}
          onPointerCancel={handleMetricPointerUp}
          onKeyDown={handleMetricKeyDown}
        >
          <span
            className={`scene-blur-control__state${spatialStage.percent === 75 ? " scene-blur-control__state--patterned-world-model" : ""}`}
            aria-live="polite"
          >
            {spatialStage.percent === 75 ? (
              <>
                <span>Patterned</span>
                <span>World Model AI</span>
              </>
            ) : (
              spatialStageWords.map((word, index) => {
                if (spatialStageWords[index - 1] === "&") return null;
                if (spatialStage.percent === 0 && spatialStageWords[index - 1] === "AI") return null;
                const line =
                  spatialStage.percent === 0 && word === "AI" && spatialStageWords[index + 1] === "Causal"
                    ? `${word} ${spatialStageWords[index + 1]}`
                    : word === "&" && spatialStageWords[index + 1]
                      ? `${word} ${spatialStageWords[index + 1]}`
                      : word;
                return <span key={`${line}-${index}`}>{line}</span>;
              })
            )}
          </span>
        </span>
      </div>
      <span className="scene-blur-control__label scene-blur-control__label--blur">
        How
        <br />
        AI Senses
        <br />
        the World
      </span>
      <button
        type="button"
        className="scene-blur-control__toggle scene-blur-control__toggle--collapse"
        aria-label="Collapse scene focus control"
        aria-expanded="true"
        onClick={() => {
          if (externallyCollapsed) return;
          stopMovementHint();
          onChange?.(100);
          setIsManuallyCollapsed(true);
        }}
      >
        <span className="scene-blur-control__toggle-symbol scene-blur-control__toggle-symbol--minus" aria-hidden="true" />
      </button>
      <button
        type="button"
        className="scene-blur-control__toggle scene-blur-control__toggle--open"
        ref={openToggleRef}
        aria-label="Open scene focus control"
        aria-expanded="false"
        onClick={() => {
          if (externallyCollapsed) return;
          onChange?.(80);
          setIsManuallyCollapsed(false);
        }}
      >
        <span className="scene-blur-control__toggle-symbol" aria-hidden="true">+</span>
      </button>
    </div>
  );
}
